import React, { useCallback } from 'react';
import { useNotifications } from '../../feedback/NotificationProvider';
import type { OrderTagProps, TagSize } from './OrderTag.types';

const sizeClasses: Record<TagSize, string> = {
  xs: 'text-xs px-1.5 py-0.5',
  sm: 'text-sm px-2 py-0.5',
  md: 'text-base px-2.5 py-1'
};

/**
 * OrderTag - Exibe ID do pedido no formato [O-XXX]
 * 
 * Cores: Amarelo vibrante (yellow-400)
 * Funcionalidade: Click para copiar com toast de confirmação
 */
export const OrderTag: React.FC<OrderTagProps> = ({
  orderId,
  size = 'sm',
  copyable = true,
  className = ''
}) => {
  const { notify } = useNotifications();

  const handleCopy = useCallback(async (e: React.MouseEvent) => {
    if (!copyable) return;
    
    e.stopPropagation();
    
    try {
      await navigator.clipboard.writeText(String(orderId));
      notify({
        type: 'success',
        title: 'ID Copiado',
        message: `Pedido O-${orderId} copiado para área de transferência`,
        sticky: false
      });
    } catch (err) {
      notify({
        type: 'error',
        title: 'Erro ao copiar',
        message: 'Não foi possível copiar o ID',
        sticky: false
      });
    }
  }, [orderId, copyable, notify]);

  return (
    <span
      className={`
        inline-flex items-center font-mono font-semibold 
        rounded border select-none whitespace-nowrap flex-shrink-0
        bg-yellow-400/20 text-yellow-300 border-yellow-400/50
        ${copyable ? 'cursor-pointer hover:bg-yellow-400/30 hover:border-yellow-400/70 active:scale-95' : ''}
        transition-all duration-150
        ${sizeClasses[size]}
        ${className}
      `}
      title={copyable ? `Pedido #${orderId} (clique para copiar)` : `Pedido #${orderId}`}
      onClick={copyable ? handleCopy : undefined}
      role={copyable ? 'button' : undefined}
      tabIndex={copyable ? 0 : undefined}
    >
      O-{orderId}
    </span>
  );
};
